import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { FolderKanban, Plus } from 'lucide-react';
import { Badge } from '@/components/badge';
import { Button } from '@/components/button';
import { Card, CardBody, CardHeader } from '@/components/card';
import { Dialog } from '@/components/dialog';
import { Input } from '@/components/input';
import { Select } from '@/components/select';
import { Textarea } from '@/components/textarea';
import { EmptyState } from '@/components/empty-state';
import { Pager } from '@/components/pager';
import { useClientProjects, useCreateProject, useSetProjectStatus } from '@/lib/projects';
import { useFormError } from '@/lib/form-error';
import { PROJECT_TYPE_LABEL, DEFAULT_PROJECT_TYPE } from '@/lib/project-types';
import { ProjectStatusSelect } from '@/components/project-status-select';

export function ClientProjects({ clientId }: { clientId: number }) {
  const [page, setPage] = useState(1);
  const { data: paginated, isLoading } = useClientProjects(clientId, page);
  const setStatus = useSetProjectStatus();
  const data = paginated?.data;
  const meta = paginated?.meta;
  const [open, setOpen] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setOpen(true)}>
          <Plus className="size-4" /> New project
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-[var(--color-foreground-muted)]">Loading…</p>
      ) : !data || data.length === 0 ? (
        <EmptyState
          icon={FolderKanban}
          title="No projects yet"
          description="Each project gets its own kanban board with todo / in progress / review / done."
        />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {data.map((p) => (
              <Link key={p.id} to={`/projects/${p.id}`}>
                <Card className="h-full transition-colors hover:bg-[var(--color-surface-muted)]/40">
                  <CardHeader>
                    <div className="font-medium">{p.name}</div>
                    {p.description ? (
                      <div className="line-clamp-2 text-xs text-[var(--color-foreground-muted)]">
                        {p.description}
                      </div>
                    ) : null}
                  </CardHeader>
                  <CardBody className="flex items-center justify-between text-xs text-[var(--color-foreground-muted)]">
                    <div className="flex items-center gap-1.5">
                      <Badge tone="neutral">{PROJECT_TYPE_LABEL[p.type] ?? p.type}</Badge>
                      <ProjectStatusSelect
                        value={p.status}
                        insideLink
                        disabled={setStatus.isPending}
                        onChange={(status) => setStatus.mutate({ id: p.id, status })}
                      />
                    </div>
                    <span>{p.cards_count} card{p.cards_count === 1 ? '' : 's'}</span>
                  </CardBody>
                </Card>
              </Link>
            ))}
          </div>
          {meta ? (
            <Pager
              page={meta.current_page}
              lastPage={meta.last_page}
              total={meta.total}
              from={meta.from}
              to={meta.to}
              onChange={setPage}
            />
          ) : null}
        </>
      )}

      <NewProjectDialog clientId={clientId} open={open} onClose={() => setOpen(false)} />
    </div>
  );
}

function NewProjectDialog({
  clientId,
  open,
  onClose,
}: {
  clientId: number;
  open: boolean;
  onClose: () => void;
}) {
  const create = useCreateProject(clientId);
  const [name, setName] = useState('');
  const [type, setType] = useState<string>(DEFAULT_PROJECT_TYPE);
  const [description, setDescription] = useState('');
  const { error, wrap } = useFormError();

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!name) return;
    const ok = await wrap(() =>
      create.mutateAsync({ name, description: description || undefined, type }),
    );
    if (ok) {
      setName('');
      setType(DEFAULT_PROJECT_TYPE);
      setDescription('');
      onClose();
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title="New project">
      <form className="space-y-3" onSubmit={handleSubmit}>
        <Input
          autoFocus
          required
          placeholder="Project name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Select value={type} onChange={(e) => setType(e.target.value)}>
          {Object.entries(PROJECT_TYPE_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
        <Textarea
          placeholder="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
        <div className="flex justify-end">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create project'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
